import { useContext, useEffect, useRef, useState } from 'react';
import {
  MobileTtsContainer,
  MobileTtsControls,
  SelectWrapper,
  SpeakerModalContent,
  SpeakerModalHeader,
  TtsTextbox,
} from './styles';
import { Modal } from '@fluentui/react';
import {
  useId,
  Dropdown,
  Option,
  FluentProvider,
} from '@fluentui/react-components';
import { BitButton } from '../../BitButton';
import { PlayCircle, Users, X } from '@phosphor-icons/react';
import { Speaker } from '../Toolbox/SpeakerModal';
import { callSpeechFunction } from '../../../lib/Helpers';
import { TtsContext } from '../../../contexts/TtsContext';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';

interface MobileTtsProps {
  tid: string;
  currentUser: string;
}

export const MobileTts = ({ tid, currentUser }: MobileTtsProps) => {
  const appInsights = useAppInsightsContext();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const dropdownId = useId('speakers-dropdown-mobile');
  const [speakers, setSpeakers] = useState<Speaker[]>();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([
    'en-US-JennyNeural',
  ]);
  const [selectedSpeaker, setSelectedSpeaker] = useState({
    key: 'Jenny',
    locale: 'English (United States)',
    text: 'en-US-JennyNeural',
    name: 'Microsoft Server Speech Text to Speech Voice (en-US, JennyNeural)',
  });
  const textRef = useRef<HTMLTextAreaElement>(null);

  const { speechToken } = useContext(TtsContext);

  useEffect(() => {
    let isMounted = true;

    if (speechToken) {
      callSpeechFunction(
        'cognitiveservices/voices/list',
        speechToken.token,
        speechToken.region
      )
        .then((data) => {
          const fetchedSpeakers: Speaker[] = data.map(
            (voice: any, index: number) => {
              return {
                key: voice.DisplayName,
                text: voice.ShortName,
                locale: voice.LocaleName,
                name: voice.Name,
              };
            }
          );

          if (isMounted) {
            setSpeakers(fetchedSpeakers);
          }
        })
        .catch((error) => {
          console.error('Speech errors:', error);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [speechToken]);

  function onSelectSpeaker(ev: any, data: any) {
    setSelectedOptions(data.selectedOptions);

    const speaker = speakers?.find((s) => s.text === data.optionValue);
    speaker && setSelectedSpeaker(speaker);
    setIsModalOpen(false);
  }

  function onPlay() {
    try {
      if (!speechToken || !selectedSpeaker || !textRef.current) return;

      const text = textRef.current.value;

      const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
        speechToken.token,
        speechToken.region
      );

      speechConfig.speechSynthesisVoiceName = selectedSpeaker.name;

      speechConfig.speechSynthesisOutputFormat =
        SpeechSDK.SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3;

      const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig);

      synthesizer.synthesisCompleted = (s, e) => {
        s.close();
      };

      synthesizer.SynthesisCanceled = (s, e) => {
        s.close();
      };

      synthesizer.speakTextAsync(text);

      if (process.env.REACT_APP_APP_INSIGHTS !== '')
        appInsights.trackEvent(
          { name: 'Audio Synthesis iniated' },
          {
            tid: tid,
            uid: currentUser,
            datasize: text.length,
          }
        );
    } catch (error: any) {
      if (process.env.REACT_APP_APP_INSIGHTS !== '')
        appInsights.trackException({
          exception: error,
          properties: {
            component: 'TtsApp',
            location: 'onGenerateAudio',
            isAudioready: false,
          },
        });
    }
  }

  return (
    <>
      <MobileTtsContainer>
        <MobileTtsControls>
          <BitButton
            label={selectedSpeaker?.key ?? 'Speaker'}
            variant="secondary"
            action={() => setIsModalOpen(true)}
            icon={<Users weight="duotone" />}
          />
          <BitButton
            label="Play"
            action={() => onPlay()}
            icon={<PlayCircle weight="duotone" />}
          />
        </MobileTtsControls>
        <TtsTextbox ref={textRef} />
      </MobileTtsContainer>
      <Modal
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(!isModalOpen)}
        isBlocking={false}
      >
        <FluentProvider>
          <SpeakerModalContent>
            <button
              className="closeModal"
              onClick={() => setIsModalOpen(false)}
            >
              <X aria-label="Close popup modal" size={20} />
            </button>
            <SpeakerModalHeader>
              <h1>Set Speaker</h1>
              <h2>Select a voice to speak your sentence</h2>
            </SpeakerModalHeader>

            <SelectWrapper>
              <span>Speakers</span>
              <Dropdown
                aria-labelledby={dropdownId}
                placeholder="Select a speaker"
                appearance="underline"
                selectedOptions={selectedOptions}
                onOptionSelect={onSelectSpeaker}
              >
                {speakers &&
                  speakers.map((option) => (
                    <Option
                      key={option.key}
                      text={option.key}
                      value={option.text}
                    >
                      {option.key} | {option.locale}
                    </Option>
                  ))}
              </Dropdown>
            </SelectWrapper>
          </SpeakerModalContent>
        </FluentProvider>
      </Modal>
    </>
  );
};
